/**
 * AdminMuhasebeListesi.jsx — Öğrenci Muhasebe Listesi (Faz L, admin/superadmin).
 *
 * Route: /admin/muhasebe-listesi (yalnız admin/superadmin — App.js RoleRoute).
 * Backend: routes/admin_muhasebe_listesi.py
 *   GET /admin/muhasebe-listesi         → filtreli satır listesi + özet (sayfalı)
 *   GET /admin/muhasebe-listesi/export  → aynı filtrelerle .xlsx indirme
 *
 * Satır = bir taksit (borç) + eşleşen ödeme; backbone tarih tipine göre
 * (vade/ödeme/kayıt) değişir — bkz. backend/services/muhasebe_listesi_
 * service.py modül docstring'i. Bu sayfa salt görüntüleme/filtreleme/export'tur,
 * hiçbir yazma işlemi yapmaz.
 *
 * Filtreler yalnızca "Listele" butonuna basınca uygulanır (otomatik değil) —
 * proje-yoneticisi Faz L brief kararı. Sertifika dropdown'ı GET /admin/
 * certificates/types'tan (cert_id → backend'in certificate_type_id filtresiyle
 * birebir eşleşir, bkz. AdminStudentSalePage.jsx'teki aynı kaynak).
 *
 * Düzeltme turu (2026-09-28, madde 2) — İLK AÇILIŞTA SORGU YOK: sayfa
 * açılınca liste otomatik ÇEKİLMEZ (`appliedFilters` başlangıçta `null`,
 * mount useEffect'i bu yüzden fetch ATLAR). Tarih aralığı (başlangıç +
 * bitiş) seçilmeden "Listele"/"Excel'e Aktar" butonları PASİF — backend de
 * AYNI kuralı uygular (`routes/admin_muhasebe_listesi.py`, date_from/date_to
 * olmadan 400 `tarih_araligi_zorunlu`); bu frontend guard'ı yalnızca UX
 * katmanıdır, tek koruma DEĞİL.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Download,
  Loader2,
  AlertTriangle,
  Search,
  Wallet,
  Banknote,
  ArrowRightLeft,
  Receipt,
  ReceiptText,
  Scale as ScaleIcon,
} from 'lucide-react';
import AdminLayout from '../components/admin/AdminLayout';
import {
  PageHeader,
  DataTable,
  DateRange,
  RichSelect,
} from '../components/admin';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { apiClient } from '@/services/apiClient';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { fmtTRY } from '../utils/formatCurrency';
import { useDanismanlarOptions, danismanOptionsList } from '../hooks/useDanismanlarOptions';
import { useAccountingBanks, useMuhasebeTipiOptions } from '@/api/queries/accounting';

const LIMIT = 50;

// ── Küçük yardımcılar ─────────────────────────────────────────────────────────

function fmtDate(d, lang) {
  if (!d) return '—';
  const parsed = new Date(d);
  if (Number.isNaN(parsed.getTime())) return d;
  return parsed.toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US');
}

function toIsoDate(date) {
  if (!date) return undefined;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const DURUM_BORDER = {
  odendi: 'border-emerald-500',
  gecikmis: 'border-red-500',
  odenmedi: 'border-gray-300',
};

// ── Toggle grup (radio benzeri buton grubu) ────────────────────────────────────
function ToggleGroup({ value, onChange, options, ariaLabel }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <motion.button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => onChange(opt.value)}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold border-2 transition-colors ${
              active
                ? 'border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 text-[var(--brand-primary)]'
                : 'border-[var(--border-light)] bg-white text-[var(--text-muted)] hover:border-[var(--brand-primary)]'
            }`}
          >
            {opt.label}
          </motion.button>
        );
      })}
    </div>
  );
}

function FilterLabel({ children }) {
  return (
    <p className="text-[11px] font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">
      {children}
    </p>
  );
}

function SummaryCard({ label, value, color, bg, icon: Icon }) {
  return (
    <div className="rounded-2xl p-4 border-2 flex items-center gap-3" style={{ background: bg, borderColor: `${color}33` }}>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: `${color}22` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-base font-bold truncate" style={{ color }}>{value}</p>
        <p className="text-[11px] font-bold" style={{ color }}>{label}</p>
      </div>
    </div>
  );
}

// ── Ana sayfa ────────────────────────────────────────────────────────────────

export default function AdminMuhasebeListesi() {
  const { t, lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();

  // ── Filtre taslağı (henüz uygulanmamış) ─────────────────────────────────────
  const [dateType, setDateType] = useState('vade');
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [onlyMoka, setOnlyMoka] = useState(false);
  const [onlyLegal, setOnlyLegal] = useState(false);
  const [senetDurumu, setSenetDurumu] = useState('tum');
  const [paymentMethod, setPaymentMethod] = useState('hepsi');
  const [courseId, setCourseId] = useState(null);
  const [certificateTypeId, setCertificateTypeId] = useState(null);
  const [danismanId, setDanismanId] = useState(null);
  const [bankId, setBankId] = useState(null);
  const [muhasebeTipi, setMuhasebeTipi] = useState(null);

  // ── Uygulanan filtreler (yalnızca "Listele" ile güncellenir) ────────────────
  const buildFilters = useCallback(() => ({
    date_type: dateType,
    date_from: toIsoDate(dateRange?.from),
    date_to: toIsoDate(dateRange?.to),
    senet_durumu: senetDurumu,
    payment_method: paymentMethod,
    course_id: courseId || undefined,
    certificate_type_id: certificateTypeId || undefined,
    danisman_id: danismanId || undefined,
    bank_id: bankId || undefined,
    muhasebe_tipi: muhasebeTipi || undefined,
    only_moka: onlyMoka || undefined,
    only_legal: onlyLegal || undefined,
  }), [dateType, dateRange, senetDurumu, paymentMethod, courseId, certificateTypeId, danismanId, bankId, muhasebeTipi, onlyMoka, onlyLegal]);

  // Düzeltme turu (2026-09-28, madde 2) — başlangıçta `null`: "henüz hiç
  // arama yapılmadı" durumunu temsil eder, aşağıdaki fetch effect'i bu
  // yüzden mount'ta ÇALIŞMAZ (otomatik sorgu yok).
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [page, setPage] = useState(1);
  const hasDateRange = Boolean(dateRange?.from && dateRange?.to);
  const hasSearched = appliedFilters !== null;

  // ── Seçenek kaynakları ───────────────────────────────────────────────────────
  const [courses, setCourses] = useState([]);
  const [certTypes, setCertTypes] = useState([]);
  const { danismanlar } = useDanismanlarOptions(authHeaders);
  const { data: banks = [] } = useAccountingBanks();
  const { data: muhasebeTipiDefs = [] } = useMuhasebeTipiOptions();

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get('/admin/courses?limit=500', { headers: authHeaders() })
      .then((r) => { if (!cancelled) setCourses(r.data?.courses || []); })
      .catch(() => { if (!cancelled) setCourses([]); });
    apiClient
      .get('/admin/certificates/types', { headers: authHeaders() })
      .then((r) => { if (!cancelled) setCertTypes(r.data?.certificates || []); })
      .catch(() => { if (!cancelled) setCertTypes([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const courseOptions = courses.map((c) => ({ value: c.course_id, label: c.title || c.title_tr || c.course_id }));
  const certOptions = certTypes.map((c) => ({ value: c.cert_id, label: lang === 'tr' ? c.name : (c.name_en || c.name) }));
  const danismanOptions = danismanOptionsList(danismanlar, (a, b) => (lang === 'tr' ? a : b));
  const bankOptions = banks.map((b) => ({ value: b.bank_id, label: b.name }));
  const muhasebeTipiOptions = muhasebeTipiDefs.map((d) => ({
    value: d.value,
    label: lang === 'tr' ? d.label_tr : (d.label_en || d.label_tr),
  }));

  // ── Liste verisi ─────────────────────────────────────────────────────────────
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  // Düzeltme turu (2026-09-28, madde 2) — başlangıçta `false`: mount'ta hiç
  // sorgu atılmadığı için "yükleniyor" skeleton'ı da gösterilmemeli.
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const requestSeq = useRef(0);

  const fetchRows = useCallback((filters, currentPage) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);
    const params = { ...filters, skip: (currentPage - 1) * LIMIT, limit: LIMIT };
    apiClient
      .get('/admin/muhasebe-listesi', { headers: authHeaders(), params })
      .then((res) => {
        if (seq !== requestSeq.current) return;
        setRows(res.data?.rows || []);
        setTotal(res.data?.total || 0);
        setSummary(res.data?.summary || null);
      })
      .catch((err) => {
        if (seq !== requestSeq.current) return;
        setError(getApiErrorMessage(err, lang));
        setRows([]);
        setSummary(null);
      })
      .finally(() => {
        if (seq !== requestSeq.current) return;
        setLoading(false);
      });
  }, [authHeaders, lang]);

  useEffect(() => {
    // Düzeltme turu (2026-09-28, madde 2) — `appliedFilters` yalnızca
    // "Listele" tıklanınca `null`'dan çıkar; mount'ta ve draft filtre
    // değişikliklerinde (appliedFilters set edilmeden) bu effect fetch
    // ATMAZ — otomatik sorgu yasağı burada uygulanıyor.
    if (appliedFilters === null) return;
    fetchRows(appliedFilters, page);
  }, [appliedFilters, page, fetchRows]);

  const handleListele = () => {
    if (!hasDateRange) return; // buton zaten disabled, savunma amaçlı
    setPage(1);
    setAppliedFilters(buildFilters());
  };

  const handleExport = async () => {
    if (exporting || !hasDateRange) return; // buton zaten disabled, savunma amaçlı
    setExporting(true);
    try {
      const res = await apiClient.get('/admin/muhasebe-listesi/export', {
        headers: authHeaders(),
        // Düzeltme turu (2026-09-28, madde 2) — export artık en son
        // "Listele" ile uygulanmış filtreleri DEĞİL, ekrandaki GÜNCEL taslak
        // filtreleri kullanır: kullanıcı Listele'ye hiç basmadan doğrudan
        // Excel'e Aktar'a basabilmeli (tarih aralığı seçili olduğu sürece).
        params: { ...buildFilters(), format: 'xlsx' },
        responseType: 'blob',
      });
      const cd = res.headers?.['content-disposition'] || '';
      const match = cd.match(/filename[^;=\n]*=["']?([^"';\n]+)["']?/i);
      const today = new Date().toISOString().slice(0, 10);
      const filename = match ? match[1].trim() : `academy-pro-muhasebe-listesi-${today}.xlsx`;

      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setExporting(false);
    }
  };

  // ── Tablo kolonları ──────────────────────────────────────────────────────────
  const displayRows = rows.map((r, i) => ({ ...r, _display_index: (page - 1) * LIMIT + i + 1 }));

  const columns = [
    {
      key: '_display_index',
      label: '#',
      width: '56px',
      render: (val, row) => (
        <div className={`border-l-4 pl-2 -ml-2 font-bold text-xs text-[var(--text-muted)] ${DURUM_BORDER[row.durum] || DURUM_BORDER.odenmedi}`}>
          {val}
        </div>
      ),
    },
    {
      key: 'student_name',
      label: t('admin.muhasebeListesi.col_student'),
      render: (val, row) => (
        <div className="min-w-0">
          {row.user_id ? (
            <Link to={`/admin/students/${row.user_id}`} className="font-bold text-xs text-[var(--brand-primary)] hover:underline">
              {val || '—'}
            </Link>
          ) : (
            <span className="font-bold text-xs">{val || '—'}</span>
          )}
          {row.student_phone && (
            <p className="text-[11px] text-[var(--text-muted)]">{row.student_phone}</p>
          )}
          <div className="flex flex-wrap gap-1 mt-1">
            {row.course_title && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-sky-100 text-sky-700">{row.course_title}</span>
            )}
            {row.danisman_adi && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700">{row.danisman_adi}</span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'borc_tarihi',
      label: t('admin.muhasebeListesi.col_debt_date'),
      render: (val) => <span className="text-xs">{fmtDate(val, lang)}</span>,
    },
    {
      key: 'borc_tutari',
      label: t('admin.muhasebeListesi.col_debt_amount'),
      render: (_val, row) => <span className="text-xs font-bold">{fmtTRY(row.borc_tutari)}</span>,
    },
    {
      key: 'odeme_tarihi',
      label: t('admin.muhasebeListesi.col_payment_date'),
      render: (val) => <span className="text-xs">{fmtDate(val, lang)}</span>,
    },
    {
      key: 'odeme_tutari_brut',
      label: t('admin.muhasebeListesi.col_payment_amount'),
      render: (_val, row) => <span className="text-xs font-bold text-emerald-600">{fmtTRY(row.odeme_tutari_brut)}</span>,
    },
    {
      key: 'odeme_tutari_aktarilan',
      label: t('admin.muhasebeListesi.col_payment_transferred'),
      render: (_val, row) => <span className="text-xs font-bold">{fmtTRY(row.odeme_tutari_aktarilan)}</span>,
    },
    {
      key: 'kdv_tutari',
      label: t('admin.muhasebeListesi.col_vat_amount'),
      render: (_val, row) => <span className="text-xs">{fmtTRY(row.kdv_tutari)}</span>,
    },
    {
      key: 'banka',
      label: t('admin.muhasebeListesi.col_bank'),
      render: (val) => <span className="text-xs">{val || '—'}</span>,
    },
    {
      key: 'senet_no',
      label: t('admin.muhasebeListesi.col_senet_no'),
      render: (val) => <span className="text-xs font-mono">{val || '—'}</span>,
    },
    {
      key: 'avukat',
      label: t('admin.muhasebeListesi.col_lawyer'),
      render: (val) => <span className="text-xs">{val || '—'}</span>,
    },
  ];

  const summaryCards = summary ? [
    { label: t('admin.muhasebeListesi.summary_total_debt'), value: fmtTRY(summary.toplam_borc), color: '#DC2626', bg: '#FEF2F2', icon: Receipt },
    { label: t('admin.muhasebeListesi.summary_total_paid'), value: fmtTRY(summary.toplam_odenen), color: '#10B981', bg: '#D7FFB8', icon: Wallet },
    { label: t('admin.muhasebeListesi.summary_total_transferred'), value: fmtTRY(summary.toplam_aktarilan), color: '#0EA5E9', bg: '#E8F5FD', icon: ArrowRightLeft },
    { label: t('admin.muhasebeListesi.summary_remaining'), value: fmtTRY(summary.kalan), color: '#E5B400', bg: '#FFF4CC', icon: Banknote },
    { label: t('admin.muhasebeListesi.summary_total_vat'), value: fmtTRY(summary.toplam_kdv), color: '#8B5CF6', bg: '#F3E8FF', icon: ReceiptText },
    { label: t('admin.muhasebeListesi.summary_vat_free_transferred'), value: fmtTRY(summary.kdvsiz_aktarilan), color: '#64748B', bg: '#F1F5F9', icon: ScaleIcon },
  ] : [];

  return (
    <AdminLayout title="Student Accounting List" titleTr={t('admin.muhasebeListesi.page_title')}>
      <PageHeader
        title={t('admin.muhasebeListesi.page_title')}
        subtitle={t('admin.muhasebeListesi.page_subtitle')}
        breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: t('admin.muhasebeListesi.page_title') }]}
      />

      {/* ── Filtre paneli ─────────────────────────────────────────────────── */}
      <div className="card-clay p-4 mb-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <FilterLabel>{t('admin.muhasebeListesi.filter_date_type')}</FilterLabel>
            <ToggleGroup
              ariaLabel={t('admin.muhasebeListesi.filter_date_type')}
              value={dateType}
              onChange={setDateType}
              options={[
                { value: 'odeme', label: t('admin.muhasebeListesi.date_type_odeme') },
                { value: 'vade', label: t('admin.muhasebeListesi.date_type_vade') },
                { value: 'kayit', label: t('admin.muhasebeListesi.date_type_kayit') },
              ]}
            />
          </div>
          <div>
            <FilterLabel>{t('admin.muhasebeListesi.filter_senet_durumu')}</FilterLabel>
            <ToggleGroup
              ariaLabel={t('admin.muhasebeListesi.filter_senet_durumu')}
              value={senetDurumu}
              onChange={setSenetDurumu}
              options={[
                { value: 'tum', label: t('admin.muhasebeListesi.senet_durumu_tum') },
                { value: 'borclu', label: t('admin.muhasebeListesi.senet_durumu_borclu') },
                { value: 'odenen', label: t('admin.muhasebeListesi.senet_durumu_odenen') },
              ]}
            />
          </div>
          <div>
            <FilterLabel>{t('admin.muhasebeListesi.filter_payment_method')}</FilterLabel>
            <ToggleGroup
              ariaLabel={t('admin.muhasebeListesi.filter_payment_method')}
              value={paymentMethod}
              onChange={setPaymentMethod}
              options={[
                { value: 'hepsi', label: t('admin.muhasebeListesi.payment_method_hepsi') },
                { value: 'cash', label: t('admin.muhasebeListesi.payment_method_cash') },
                { value: 'credit_card', label: t('admin.muhasebeListesi.payment_method_credit_card') },
                { value: 'bank_transfer', label: t('admin.muhasebeListesi.payment_method_bank_transfer') },
              ]}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-5">
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={onlyMoka} onCheckedChange={(v) => setOnlyMoka(!!v)} />
            <span className="text-xs font-semibold">{t('admin.muhasebeListesi.filter_only_moka')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={onlyLegal} onCheckedChange={(v) => setOnlyLegal(!!v)} />
            <span className="text-xs font-semibold">{t('admin.muhasebeListesi.filter_only_legal')}</span>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <div>
            <FilterLabel>{t('admin.muhasebeListesi.filter_course')}</FilterLabel>
            <RichSelect
              options={courseOptions}
              value={courseId}
              onChange={setCourseId}
              placeholder={t('admin.muhasebeListesi.filter_all')}
            />
          </div>
          <div>
            <FilterLabel>{t('admin.muhasebeListesi.filter_certificate')}</FilterLabel>
            <RichSelect
              options={certOptions}
              value={certificateTypeId}
              onChange={setCertificateTypeId}
              placeholder={t('admin.muhasebeListesi.filter_all')}
            />
          </div>
          <div>
            <FilterLabel>{t('admin.muhasebeListesi.filter_danisman')}</FilterLabel>
            <RichSelect
              options={danismanOptions}
              value={danismanId}
              onChange={setDanismanId}
              placeholder={t('admin.muhasebeListesi.filter_all')}
            />
          </div>
          <div>
            <FilterLabel>{t('admin.muhasebeListesi.filter_bank')}</FilterLabel>
            <RichSelect
              options={bankOptions}
              value={bankId}
              onChange={setBankId}
              placeholder={t('admin.muhasebeListesi.filter_all')}
            />
          </div>
          <div>
            <FilterLabel>{t('admin.muhasebeListesi.filter_muhasebe_tipi')}</FilterLabel>
            <RichSelect
              options={muhasebeTipiOptions}
              value={muhasebeTipi}
              onChange={setMuhasebeTipi}
              placeholder={t('admin.muhasebeListesi.filter_all')}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[260px]">
            <FilterLabel>{t('admin.muhasebeListesi.filter_date_range')}</FilterLabel>
            <DateRange value={dateRange} onChange={setDateRange} shortcuts={['today', 'last7', 'last30', 'thisMonth', 'lastMonth']} />
          </div>
          <motion.div whileHover={{ scale: hasDateRange ? 1.02 : 1 }} whileTap={{ scale: hasDateRange ? 0.98 : 1 }}>
            <Button onClick={handleListele} disabled={loading || !hasDateRange} className="btn-primary h-9 px-5">
              {loading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
              {t('admin.muhasebeListesi.btn_list')}
            </Button>
          </motion.div>
          <motion.div whileHover={{ scale: hasDateRange ? 1.02 : 1 }} whileTap={{ scale: hasDateRange ? 0.98 : 1 }}>
            <Button onClick={handleExport} disabled={exporting || !hasDateRange} variant="outline" className="h-9 px-5 rounded-xl border-2 border-[var(--border-light)] font-bold text-sm">
              {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
              {t('admin.muhasebeListesi.btn_export')}
            </Button>
          </motion.div>
          {/* Düzeltme turu (2026-09-28, madde 2) — tarih aralığı seçilmeden
              butonlar pasif; kısa uyarı metni. */}
          {!hasDateRange && (
            <p className="text-[11px] font-semibold text-amber-600 flex items-center gap-1">
              <AlertTriangle size={13} className="shrink-0" />
              {t('admin.muhasebeListesi.warning_date_range_required')}
            </p>
          )}
        </div>
      </div>

      {/* ── Özet kartları ─────────────────────────────────────────────────── */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
          {summaryCards.map((s, i) => <SummaryCard key={i} {...s} />)}
        </div>
      )}

      {/* ── Hata durumu ───────────────────────────────────────────────────── */}
      {error && (
        <div className="card-clay p-4 mb-4 flex items-center gap-3 border-red-200 bg-red-50">
          <AlertTriangle size={18} className="text-red-500 shrink-0" />
          <p className="text-sm font-semibold text-red-600">{error}</p>
        </div>
      )}

      {/* ── Tablo ─────────────────────────────────────────────────────────── */}
      <DataTable
        columns={columns}
        data={displayRows}
        loading={loading}
        rowKey="row_id"
        pageSize={LIMIT}
        emptyTitle={t('admin.muhasebeListesi.empty_title')}
        emptyDescription={
          hasSearched
            ? t('admin.muhasebeListesi.empty_desc')
            : t('admin.muhasebeListesi.empty_desc_no_range')
        }
        serverPagination
        currentPage={page}
        totalItems={total}
        onPageChange={(p) => setPage(p)}
      />
    </AdminLayout>
  );
}
